import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CloudRain,
  Wrench,
  ShieldAlert,
  ClipboardCheck,
  Users,
  Activity,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { useOperationsFeed } from "../lib/operationsFeed";
import { useT } from "../lib/i18n";

/**
 * OperationalActivityTicker
 * --------------------------------------------------
 * Industrial, mission-critical event ticker. Scrolls horizontally in a
 * single restrained line — NO flashing, NO arcade-style animation, NO
 * cyberpunk effects. Looks like a Bloomberg Terminal / NOC operations
 * panel: dense, mono-typed, color-coded only by tone (red/amber/green),
 * with a steady marquee.
 *
 * Each event is a structured operational event:
 *   - time stamp (HH:MM)
 *   - severity (critical / warn / info / ok)
 *   - source (job code / equipment / crew / role)
 *   - event line
 *
 * Events are seeded for demo authenticity. In a live tenant these come
 * from the activity log feed (incidents, pre-op fails, weather holds,
 * routing notifications, expired certs, etc.).
 */

const EVENTS = [
  { sev: "critical", icon: ShieldAlert,    src: "INC-2042 · I-45 LANE 2",    line: "Incident escalated · safety lead notified · corrective action queued" },
  { sev: "warn",     icon: Wrench,         src: "CAT 336 · UNIT 1142",       line: "Pre-Op FAIL · hydraulic leak L-side · unit locked out" },
  { sev: "warn",     icon: CloudRain,      src: "JOB 2104 · SHIFT 02",       line: "Weather hold · 38m · wind 31mph · resume 14:45" },
  { sev: "warn",     icon: Clock,          src: "CREW B-2 · 4 OPS",          line: "Overdue safety acknowledgments · 3 crew · expires 16:00" },
  { sev: "info",     icon: ClipboardCheck, src: "DR-5511 · JOB 2098",        line: "Daily Report submitted · routed to PM · PDF generated" },
  { sev: "warn",     icon: Users,          src: "JOB 2110 · CREW A-1",       line: "Manpower short · 2 ops · backfill request open" },
  { sev: "critical", icon: AlertTriangle,  src: "OP 4471 · CERT EXPIRED",    line: "OSHA-30 cert expired 03d ago · op flagged · training auto-assigned" },
  { sev: "info",     icon: Activity,       src: "PM JONES · BACKLOG",        line: "4 approvals pending · oldest 22h · escalation T-2h" },
  { sev: "ok",       icon: CheckCircle2,   src: "JOB 2105 · QA-318",         line: "Inspection passed · routed · archive logged" },
  { sev: "warn",     icon: Wrench,         src: "EXC 320 · UNIT 0913",       line: "Repair in progress · ETA RTS 11:30 · awaiting part" },
  { sev: "info",     icon: ClipboardCheck, src: "TT-887 · CREW C-3",         line: "Toolbox talk submitted · 7 attended · auto-routed to crew profiles" },
  { sev: "critical", icon: ShieldAlert,    src: "INC-2043 · TRENCH OPS",     line: "Stop-the-line · trench box damage · superintendent en-route" },
  { sev: "info",     icon: Users,          src: "ONB-014 · OP STARTED",      line: "Onboarding pack issued · QR scanned · packet EN delivered" },
];

const TONE = {
  critical: { color: "#FF6A6A", label: "CRITICAL" },
  warn:     { color: "#FFB000", label: "WARNING"  },
  info:     { color: "#FF6A00", label: "ROUTING"  },
  ok:       { color: "#65DA76", label: "OK"       },
};

// Map a kind string from the backend feed to the right icon for the rail.
const ICON_FOR_KIND = {
  "incident":       ShieldAlert,
  "preop-fail":     Wrench,
  "weather-hold":   CloudRain,
  "daily-report":   ClipboardCheck,
  "toolbox-talk":   ClipboardCheck,
  "qaqc":           CheckCircle2,
  "manpower":       Users,
  "cert-expired":   AlertTriangle,
  "approval":       Activity,
  "overdue":        Clock,
};
function iconForKind(k) {
  return ICON_FOR_KIND[(k || "").toLowerCase()] || Activity;
}

function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// Map an inbound `forgeops:submission` kind to a believable ticker event.
function buildLiveEvent(detail) {
  const kind = (detail?.kind || "").toLowerCase();
  if (kind === "toolbox-talk") {
    return {
      sev:  "info",
      icon: ClipboardCheck,
      src:  `TT-${String(Math.floor(Math.random() * 900) + 100).padStart(3, "0")} · CREW C-3`,
      line: `Toolbox Talk submitted · ${detail?.attendees ?? 7} attended · routed to crew profiles · acks logged`,
    };
  }
  if (kind === "daily-report") {
    return {
      sev:  "info",
      icon: ClipboardCheck,
      src:  `DR-${String(Math.floor(Math.random() * 9000) + 1000)} · JOB ${detail?.jobNumber || "2098"}`,
      line: "Daily Report submitted · routed to PM · PDF generated",
    };
  }
  if (kind === "incident" || kind === "near-miss") {
    return {
      sev:  "critical",
      icon: ShieldAlert,
      src:  `INC-${String(Math.floor(Math.random() * 900) + 2050)} · FIELD`,
      line: "Incident submitted · safety paged · CA queued · training auto-assigned",
    };
  }
  if (kind === "preop-fail") {
    return {
      sev:  "warn",
      icon: Wrench,
      src:  `PRE-OP · UNIT ${String(Math.floor(Math.random() * 9000) + 1000)}`,
      line: "Pre-Op FAIL · unit locked out · shop notified · RTS pending",
    };
  }
  return null;
}

export default function OperationalActivityTicker({ compact = false }) {
  const { t } = useT();
  // Live operations feed — replaces seeded events when the backend has
  // real records. When the DB is empty (or unreachable) the backend
  // sends seed events back so the rail stays full and the demo never
  // goes blank.
  const { events: feedEvents, seeded: isSeed } = useOperationsFeed(12);

  // Normalize backend feed events into the shape the rail renders.
  const dbEvents = useMemo(() => {
    if (!Array.isArray(feedEvents) || feedEvents.length === 0) return [];
    return feedEvents.map((e) => ({
      sev:  (e.sev || "info"),
      icon: iconForKind(e.kind),
      src:  e.src || "FORGEDOPS",
      line: e.line || "",
      ts:   e.ts || null,
      _id:  e.id || `${e.kind}-${e.ts || Math.random()}`,
    }));
  }, [feedEvents]);

  // When the backend returns its own seeded fallback set, blend in the
  // local static EVENTS too so the rail always has length. When real
  // DB events arrive (seeded=false) we use them exclusively to preserve
  // operational truth (no fake noise mixed with real records).
  const baseEvents = useMemo(() => {
    if (dbEvents.length > 0 && !isSeed) return dbEvents;
    if (dbEvents.length > 0 &&  isSeed) {
      // dbEvents is the backend seed — keep it as the truth.
      return dbEvents;
    }
    // No feed yet (initial fetch) — render the local seeded set so
    // the rail is never blank on first paint.
    return EVENTS;
  }, [dbEvents, isSeed]);

  // Pre-compute stamps once per baseEvents (drifting back in time so
  // it reads as a real rolling log, not a wall of "now").
  const stamps = useMemo(() => {
    const base = new Date();
    return baseEvents.map((e, i) => {
      if (e.ts) {
        const t = new Date(e.ts);
        if (!Number.isNaN(t.getTime())) {
          return `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`;
        }
      }
      const t = new Date(base.getTime() - (i * 4 + 1) * 60000);
      return `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`;
    });
  }, [baseEvents]);
  const [now, setNow] = useState(nowHHMM());

  // Live operational event banner — appears for ~6s above the rail
  // when a real demo submission is dispatched via window event.
  // Restrained: a single line, fade in/out, no flashing.
  const [liveEvent, setLiveEvent] = useState(null);
  // Persistent live events that get prepended to the rolling rail and
  // drift naturally with the existing 80s marquee. Capped at 4 — older
  // ones rotate out so the rail never grows unbounded.
  const [injected, setInjected] = useState([]);
  useEffect(() => {
    const onSubmit = (ev) => {
      const built = buildLiveEvent(ev?.detail || {});
      if (!built) return;
      const stamp = nowHHMM();
      const id    = Date.now();
      setLiveEvent({ ...built, stamp, id });
      setInjected((prev) => [{ ...built, stamp, id }, ...prev].slice(0, 4));
    };
    window.addEventListener("forgeops:submission", onSubmit);
    return () => window.removeEventListener("forgeops:submission", onSubmit);
  }, []);

  // Auto-clear live event banner after 6 seconds.
  useEffect(() => {
    if (!liveEvent) return undefined;
    const t = setTimeout(() => setLiveEvent(null), 6000);
    return () => clearTimeout(t);
  }, [liveEvent]);

  useEffect(() => {
    const i = setInterval(() => setNow(nowHHMM()), 30000);
    return () => clearInterval(i);
  }, []);

  // Two copies for seamless loop. Prepend any injected real events.
  const combined = [...injected, ...baseEvents];
  const items = [...combined, ...combined];
  // Build stamp array matching combined order (injected events use their
  // own stamp; seeded events use the pre-computed drift array)
  const allStamps = [
    ...injected.map((e) => e.stamp),
    ...stamps,
  ];
  const stampsX2 = [...allStamps, ...allStamps];

  return (
    <section
      className="relative overflow-hidden rounded-md"
      style={{
        background: "#0A0A0A",
        border: "1px solid #1A1A1A",
      }}
      data-testid="operational-activity-ticker"
      aria-label="Live operational activity"
    >
      {/* Header strip */}
      <div
        className="flex items-center justify-between px-4 py-2 border-b"
        style={{ borderColor: "#1A1A1A", background: "#0F0F0F" }}
      >
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{
              background: "#FF6A00",
              boxShadow: "0 0 6px rgba(255,106,0,0.8)",
              animation: "fo-pulse-slow 2.4s ease-in-out infinite",
            }}
          />
          <span
            className="font-mono text-[10px] uppercase tracking-[0.3em] font-bold"
            style={{ color: "#FF6A00" }}
          >
            {t("Live Operations · System Activity")}
          </span>
        </div>
        <span
          className="font-mono text-[10px] uppercase tracking-[0.25em]"
          style={{ color: "#666" }}
        >
          {now} · {t("all crews · 3 jobs active")}
        </span>
      </div>

      {/* Live submission banner — appears briefly when a real demo
          submission fires. Restrained single-row strip. */}
      {liveEvent ? (
        <div
          key={liveEvent.id}
          className="flex items-center gap-3 px-4 py-2 border-b"
          style={{
            borderColor: "rgba(255,106,0,0.4)",
            background:  "rgba(255,106,0,0.06)",
            animation:   "fo-live-banner-in 320ms ease-out",
          }}
          data-testid="ticker-live-event"
        >
          <span
            className="font-mono text-[9px] uppercase tracking-[0.3em] font-bold px-2 py-0.5 rounded"
            style={{
              background: "#FF6A00",
              color: "#000",
            }}
          >
            {t("JUST NOW · LIVE")}
          </span>
          <span className="font-mono text-[10px] font-bold" style={{ color: "#666" }}>
            {liveEvent.stamp}
          </span>
          {(() => {
            const Icon = liveEvent.icon;
            const tone = TONE[liveEvent.sev];
            return (
              <>
                <Icon className="w-3.5 h-3.5" style={{ color: tone.color }} />
                <span
                  className="font-mono text-[10px] uppercase tracking-[0.15em] font-bold"
                  style={{ color: "#FFFFFF" }}
                >
                  {liveEvent.src}
                </span>
                <span style={{ color: "#888" }}>·</span>
                <span className="font-mono text-[11px]" style={{ color: "#E0E0E0" }}>
                  {liveEvent.line}
                </span>
              </>
            );
          })()}
        </div>
      ) : null}

      {/* Ticker rail — single industrial scroll */}
      <div
        className="relative"
        style={{
          maskImage:
            "linear-gradient(90deg, transparent 0, #000 60px, #000 calc(100% - 60px), transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(90deg, transparent 0, #000 60px, #000 calc(100% - 60px), transparent 100%)",
        }}
      >
        <div
          className="flex items-center gap-8 py-3 whitespace-nowrap will-change-transform"
          style={{
            animation: "fo-marquee 80s linear infinite",
          }}
          data-testid="ticker-rail"
        >
          {items.map((ev, i) => {
            const Icon = ev.icon;
            const tone = TONE[ev.sev];
            // Items that came from injected real submissions get a
            // subtle "LIVE" prefix chip in the rail so they read as
            // first-party operational events rather than seeded noise.
            const injectedCount = injected.length;
            const isInjected =
              (i < injectedCount) ||
              (i >= combined.length && i < combined.length + injectedCount);
            return (
              <span
                key={i}
                className="inline-flex items-center gap-2 font-mono text-[11px] tracking-wide pl-4"
                style={{ color: "#BDBDBD" }}
              >
                <span
                  className="font-bold"
                  style={{ color: "#666" }}
                >
                  {stampsX2[i]}
                </span>
                <Icon className="w-3.5 h-3.5" style={{ color: tone.color }} />
                <span
                  className="uppercase tracking-[0.2em] text-[9px] font-bold px-1.5 py-0.5 rounded"
                  style={{
                    background: `${tone.color}1F`,
                    color: tone.color,
                    border: `1px solid ${tone.color}55`,
                  }}
                >
                  {tone.label}
                </span>
                {isInjected && (
                  <span
                    className="uppercase tracking-[0.2em] text-[9px] font-bold px-1.5 py-0.5 rounded"
                    style={{
                      background: "#FF6A00",
                      color: "#0A0A0A",
                    }}
                    data-testid="ticker-injected-tag"
                  >
                    LIVE
                  </span>
                )}
                <span
                  className="uppercase tracking-[0.15em] text-[10px] font-bold"
                  style={{ color: "#FFFFFF" }}
                >
                  {ev.src}
                </span>
                <span style={{ color: "#888" }}>·</span>
                <span style={{ color: "#BDBDBD" }}>{ev.line}</span>
                <span style={{ color: "#2A2A2A" }} className="ml-4">|</span>
              </span>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes fo-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes fo-pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes fo-live-banner-in {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="ticker-rail"] { animation-play-state: paused !important; }
          [data-testid="ticker-live-event"] { animation: none !important; }
        }
      `}</style>
    </section>
  );
}
